import {
  compareDatetimeOriginTransformations,
  DatasetPropertyResponse,
  DatasetPropertyType,
  DatasetResponse,
  DatetimeOriginTransformation,
  JoinStepConditionRequest,
  JoinStepConditionResponse,
  JoinType,
  MathUnit,
  TimeSeriesGranularity,
} from 'mapistry-shared';
import { DeepPartial } from 'utility-types';
import { TimeSeriesJoinType } from './types';

const JOIN_CONDITION_DISALLOWED_PROPERTY_TYPES = [
  // Joining on dates have its special required condition component – DateIntervalCondition
  DatasetPropertyType.DATE,
  DatasetPropertyType.DATE_RANGE,
  DatasetPropertyType.DATETIME,
  // don't let users add a condition on project, while we make all joins project specific under the hood either way
  DatasetPropertyType.PROJECT,
  // joining record properties is illogical
  DatasetPropertyType.RECORD,
];

export function isTimeSeriesGranularityDateRange(
  timeSeriesGranularity: TimeSeriesGranularity,
) {
  switch (timeSeriesGranularity) {
    case TimeSeriesGranularity.DATETIME:
      return false;
    case TimeSeriesGranularity.MINUTE:
    case TimeSeriesGranularity.HOUR:
    case TimeSeriesGranularity.DAY:
    case TimeSeriesGranularity.WEEK:
    case TimeSeriesGranularity.MONTH:
    case TimeSeriesGranularity.QUARTER:
    case TimeSeriesGranularity.YEAR:
    case TimeSeriesGranularity.ANY: // it shouldn't be possible, but technically ANY granularity is a date range granularity
      return true;
    default: {
      const exhaustiveCheck: never = timeSeriesGranularity;
      return exhaustiveCheck;
    }
  }
}

export function getTimeSeriesPropertyLabel(dataset: DatasetResponse) {
  const { timeSeriesGranularity } = dataset;
  return timeSeriesGranularity &&
    isTimeSeriesGranularityDateRange(timeSeriesGranularity)
    ? 'Period'
    : 'Log Date';
}

export function getTimeSeriesJoinType(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
): TimeSeriesJoinType {
  const leftTimeSeriesGranularity = leftDataset.timeSeriesGranularity;
  const rightTimeSeriesGranularity = rightDataset.timeSeriesGranularity;

  if (
    leftTimeSeriesGranularity &&
    leftTimeSeriesGranularity === TimeSeriesGranularity.DATETIME
  ) {
    if (!rightTimeSeriesGranularity) {
      return TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES;
    }
    if (rightTimeSeriesGranularity === TimeSeriesGranularity.DATETIME) {
      return TimeSeriesJoinType.DATETIME_TO_DATETIME;
    }
    if (isTimeSeriesGranularityDateRange(rightTimeSeriesGranularity)) {
      return TimeSeriesJoinType.DATETIME_TO_DATE_RANGE;
    }
  }

  if (
    leftTimeSeriesGranularity &&
    isTimeSeriesGranularityDateRange(leftTimeSeriesGranularity)
  ) {
    if (!rightTimeSeriesGranularity) {
      return TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES;
    }
    if (rightTimeSeriesGranularity === TimeSeriesGranularity.DATETIME) {
      return TimeSeriesJoinType.DATE_RANGE_TO_DATETIME;
    }
    if (isTimeSeriesGranularityDateRange(rightTimeSeriesGranularity)) {
      return TimeSeriesJoinType.DATE_RANGE_TO_DATE_RANGE;
    }
  }

  return TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES;
}

export function isFullOuterJoinAllowed(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
) {
  return (
    (leftDataset.timeSeriesGranularity && rightDataset.timeSeriesGranularity) ||
    (!leftDataset.timeSeriesGranularity && !rightDataset.timeSeriesGranularity)
  );
}

export function isLeftOuterJoinAllowed(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
) {
  if (isFullOuterJoinAllowed(leftDataset, rightDataset)) {
    return true;
  }

  return (
    leftDataset.timeSeriesGranularity && !rightDataset.timeSeriesGranularity
  );
}

export function getTimeSeriesDateRangeProperties(dataset: DatasetResponse) {
  const timeSeriesProperty = dataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  );
  if (!timeSeriesProperty) {
    return [];
  }
  const parentTimeSeriesProperty =
    timeSeriesProperty.type === DatasetPropertyType.DATETIME
      ? timeSeriesProperty
      : dataset.schemaProperties.find(
          (p) =>
            p.id === timeSeriesProperty.parentPropertyId && !p.parentPropertyId,
        );
  if (!parentTimeSeriesProperty) {
    return [];
  }
  return dataset.schemaProperties.filter(
    (p) =>
      p.type === DatasetPropertyType.DATE_RANGE &&
      p.parentPropertyId === parentTimeSeriesProperty.id &&
      p.parentPropertyIdChain.length === 1,
  );
}

export function getTimeSeriesDateRangeByTransformation(
  dataset: DatasetResponse,
  transformation: DatetimeOriginTransformation,
) {
  const timeSeriesDateRanges = getTimeSeriesDateRangeProperties(dataset);
  return timeSeriesDateRanges.find(
    (p) => p.dateTransformation === transformation,
  );
}

export function isDateIntervalCondition(
  condition: JoinStepConditionResponse,
  inputTimeSeriesDateRanges: DatasetPropertyResponse[],
  joinToTimeSeriesDateRanges: DatasetPropertyResponse[],
): boolean {
  if (!inputTimeSeriesDateRanges.length || !joinToTimeSeriesDateRanges.length) {
    return false;
  }
  const inputDateRangePropertyIds = inputTimeSeriesDateRanges.map((p) => p.id);
  const joinToDateRangePropertyIds = joinToTimeSeriesDateRanges.map(
    (p) => p.id,
  );
  return (
    inputDateRangePropertyIds.includes(condition.leftPropertyId) &&
    joinToDateRangePropertyIds.includes(condition.rightPropertyId)
  );
}

export function doesNeedDateIntervalCondition(
  datasetDataType: TimeSeriesJoinType,
) {
  switch (datasetDataType) {
    case TimeSeriesJoinType.DATETIME_TO_DATETIME:
    case TimeSeriesJoinType.DATETIME_TO_DATE_RANGE:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATETIME:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATE_RANGE:
      return true;
    case TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES:
      return false;
    default: {
      const exhaustiveCheck: never = datasetDataType;
      throw new Error(
        `Couldn't identify whether connecting time series datasets or not - ${exhaustiveCheck}`,
      );
    }
  }
}

export function isDateConditionDisabled(datasetDataType: TimeSeriesJoinType) {
  switch (datasetDataType) {
    case TimeSeriesJoinType.DATETIME_TO_DATE_RANGE:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATETIME:
    case TimeSeriesJoinType.DATE_RANGE_TO_DATE_RANGE:
      return true;
    case TimeSeriesJoinType.DATETIME_TO_DATETIME:
    case TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES:
    case TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES:
      return false;
    default: {
      const exhaustiveCheck: never = datasetDataType;
      throw new Error(
        `Couldn't identify whether connecting time series datasets or not - ${exhaustiveCheck}`,
      );
    }
  }
}

export function getDefaultDateInterval(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
): DatetimeOriginTransformation | undefined {
  // a date interval should only be selected if both datasets have a time series date property
  if (
    !doesNeedDateIntervalCondition(
      getTimeSeriesJoinType(leftDataset, rightDataset),
    )
  ) {
    return undefined;
  }

  const leftInterval = leftDataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  )?.dateTransformation;
  const rightInterval = rightDataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  )?.dateTransformation;
  if (leftInterval && rightInterval) {
    // Selecting the largest interval of the two, e.g. year if we are joining monthly and yearly aggregated datasets.
    return compareDatetimeOriginTransformations(leftInterval, rightInterval) > 0
      ? leftInterval
      : rightInterval;
  }
  return leftInterval || rightInterval || undefined;
}

export function getDefaultJoinType(
  leftDataset: DatasetResponse,
  rightDataset: DatasetResponse,
): JoinType {
  if (leftDataset.timeSeriesGranularity && rightDataset.timeSeriesGranularity) {
    return JoinType.FULL_OUTER;
  }

  if (
    leftDataset.timeSeriesGranularity &&
    !rightDataset.timeSeriesGranularity
  ) {
    return JoinType.LEFT_OUTER;
  }

  return JoinType.INNER;
}

export function isDefaultProjectConditionEnough(
  datasetDataType: TimeSeriesJoinType,
  inputDataset: DatasetResponse,
  joinToDataset: DatasetResponse,
) {
  const isJoiningNoTimeSeriesData = [
    TimeSeriesJoinType.DATETIME_TO_NO_TIME_SERIES,
    TimeSeriesJoinType.DATE_RANGE_TO_NO_TIME_SERIES,
    TimeSeriesJoinType.NO_TIME_SERIES_TO_NO_TIME_SERIES,
  ].includes(datasetDataType);
  if (!isJoiningNoTimeSeriesData) {
    return false;
  }
  const isInputSiteSpecific = inputDataset.schemaProperties.some(
    (p) => p.type === DatasetPropertyType.PROJECT,
  );
  const isJoinToSiteSpecific = joinToDataset.schemaProperties.some(
    (p) => p.type === DatasetPropertyType.PROJECT,
  );
  return isInputSiteSpecific && isJoinToSiteSpecific;
}

export function isEffectivePeriodConditionRelevant(
  inputDataset: DatasetResponse,
  joinToDataset: DatasetResponse,
) {
  const inputGranularity = inputDataset.timeSeriesGranularity;
  if (!inputGranularity) {
    return inputDataset.isVersioned && joinToDataset.isVersioned;
  }

  const smallGranularities: TimeSeriesGranularity[] = [
    TimeSeriesGranularity.DATETIME,
    TimeSeriesGranularity.MINUTE,
    TimeSeriesGranularity.HOUR,
    TimeSeriesGranularity.DAY,
  ];
  const isSmallInputGranularity =
    inputGranularity && smallGranularities.includes(inputGranularity);

  return isSmallInputGranularity && joinToDataset.isVersioned;
}

export function areAllRegularConditionsOptional(
  datasetDataType: TimeSeriesJoinType,
  inputDataset: DatasetResponse,
  joinToDataset: DatasetResponse,
) {
  return (
    doesNeedDateIntervalCondition(datasetDataType) ||
    isDefaultProjectConditionEnough(
      datasetDataType,
      inputDataset,
      joinToDataset,
    )
  );
}

export function getDefaultConditions(
  datasetDataType: TimeSeriesJoinType,
  inputDataset: DatasetResponse,
  joinToDataset: DatasetResponse,
): DeepPartial<JoinStepConditionRequest[]> {
  if (
    areAllRegularConditionsOptional(
      datasetDataType,
      inputDataset,
      joinToDataset,
    )
  ) {
    return [];
  }
  return [{ leftPropertyId: undefined, rightPropertyId: undefined }];
}

export function getUserFriendlyResourceReferencePropertyName(
  property: DatasetPropertyResponse,
) {
  // Don't match names like "acID", "wIDe boiler", "boiler IDle", "IDle paint"
  // But do match names like "id", "id 2", "dog id", "cat id 2", "resource_id", "id2"
  // Unfortunately I think this will fail to match "jakeId" or "JAKEid".
  // But regex is hard and maybe users can stop naming things weirdly.
  if (property.fullName.toLowerCase().match(/(?<![a-zA-Z])id(?![a-zA-Z])/)) {
    return property.fullName;
  }
  return `${property.fullName} Id`;
}

export function getUserFriendlyResourceTypeDatasetName(
  resourceTypeDataset: DatasetResponse,
) {
  // Since resource is less likely to appear as a substring of another word
  // It seemed appropriate to be less thorough here.
  if (resourceTypeDataset.name.toLowerCase().includes('resource')) {
    return resourceTypeDataset.name;
  }
  return `${resourceTypeDataset.name} Resource`;
}

export function getUserFriendlyResourceIdPropertyName(
  dataset: DatasetResponse,
) {
  return `${getUserFriendlyResourceTypeDatasetName(dataset)} Id`;
}

export function getUserFriendlyPropertyLabel(
  property: DatasetPropertyResponse,
  dataset: DatasetResponse | undefined,
) {
  if (
    dataset &&
    property.type === DatasetPropertyType.UUID &&
    property.fullName === 'Resource Id'
  ) {
    return getUserFriendlyResourceIdPropertyName(dataset);
  }
  if (property.type === DatasetPropertyType.RESOURCE) {
    return getUserFriendlyResourceReferencePropertyName(property);
  }
  return property.fullName;
}

export function tryGetUnjoinedResourceReferencePropertyFromDataset({
  dataset,
  resourceTypeId,
}: {
  dataset: DatasetResponse;
  resourceTypeId: string;
}) {
  return dataset.schemaProperties?.find(
    (p) =>
      p.type === DatasetPropertyType.RESOURCE &&
      p.resourceTypeId === resourceTypeId &&
      !p.hasBeenJoinedTo,
  );
}

export function tryGetBuiltInResourceIdPropertyFromDataset({
  dataset,
  resourceTypeId,
}: {
  dataset: DatasetResponse;
  resourceTypeId: string;
}) {
  return dataset.schemaProperties?.find(
    (p) =>
      p.type === DatasetPropertyType.UUID &&
      p.resourceTypeId === resourceTypeId,
  );
}

export function tryToGetPairedPropertiesForResourceTypeJoin({
  maybeReferencingDataset,
  maybeResourceTypeDataset,
}: {
  maybeReferencingDataset: DatasetResponse;
  maybeResourceTypeDataset: DatasetResponse;
}) {
  const resourceReferenceProperty =
    tryGetUnjoinedResourceReferencePropertyFromDataset({
      dataset: maybeReferencingDataset,
      resourceTypeId: maybeResourceTypeDataset.originId,
    });
  const resourceIdProperty =
    resourceReferenceProperty?.resourceTypeId &&
    tryGetBuiltInResourceIdPropertyFromDataset({
      dataset: maybeResourceTypeDataset,
      resourceTypeId: resourceReferenceProperty.resourceTypeId,
    });

  if (resourceReferenceProperty && resourceIdProperty) {
    return {
      resourceReferenceProperty: {
        id: resourceReferenceProperty.id,
        name: getUserFriendlyResourceReferencePropertyName(
          resourceReferenceProperty,
        ),
      },
      resourceIdProperty: {
        id: resourceIdProperty.id,
        name: getUserFriendlyResourceIdPropertyName(maybeResourceTypeDataset),
      },
    };
  }
  return undefined;
}

function areStepConditionPropertiesCompatible(
  leftProperty: DatasetPropertyResponse,
  rightProperty: DatasetPropertyResponse,
) {
  if (leftProperty.resourceTypeId) {
    return rightProperty.resourceTypeId === leftProperty.resourceTypeId;
  }
  if (leftProperty.type === DatasetPropertyType.NUMERIC) {
    return (
      rightProperty.type === DatasetPropertyType.NUMERIC &&
      (leftProperty.units
        ? rightProperty.units &&
          MathUnit.areEqual(leftProperty.units, rightProperty.units)
        : !rightProperty.units)
    );
  }
  return leftProperty.type === rightProperty.type;
}

export function filterConditionProperties(
  property: DatasetPropertyResponse,
  {
    selectedPropertyId,
    leftProperty,
  }: {
    selectedPropertyId?: string;
    leftProperty?: DatasetPropertyResponse;
  },
) {
  // Always show the selected property even if we changed rules whether it can be selected or not
  if (property.id === selectedPropertyId) {
    return true;
  }
  return (
    (!leftProperty ||
      areStepConditionPropertiesCompatible(leftProperty, property)) &&
    !property.isHidden &&
    !JOIN_CONDITION_DISALLOWED_PROPERTY_TYPES.includes(property.type)
  );
}
